import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  ClipboardCheck, Loader2, ChevronDown, ChevronRight, Check,
  Package, Box, Droplets, Scissors, Wrench, Layers, Send, RotateCcw,
  ShieldCheck, X, Clock, AlertTriangle
} from "lucide-react";
import { API } from "../../App";

const CATEGORY_ICONS = {
  "Films/Wraps": Box,
  "Chemicals/Liquids": Droplets,
  "Blades/Cutting tools": Scissors,
  "Tools/Equipment": Wrench,
  "Consumables": Layers,
};

// Categories that need full/partial counting (like films)
const FULL_PARTIAL_CATEGORIES = ["Films/Wraps", "Chemicals/Liquids"];

// Categories that just need a simple count (packs/pcs)
const SIMPLE_COUNT_CATEGORIES = ["Blades/Cutting tools", "Tools/Equipment", "Consumables"];

const StockTakeSection = ({ isAdmin = false }) => {
  const [stockData, setStockData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [productCounts, setProductCounts] = useState({}); // product_id -> { full, partial } or { count }
  const [notes, setNotes] = useState("");

  // Pending reviews state (admin only). The submit + review flow is one
  // person on this deployment — surfacing pending submissions above the
  // counting UI means the admin never forgets an outstanding one.
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null); // in-flight submission_id
  const [rejectDialog, setRejectDialog] = useState(null); // submission being rejected
  const [rejectNotes, setRejectNotes] = useState("");

  const loadPendingSubmissions = useCallback(async () => {
    if (!isAdmin) return;
    setPendingLoading(true);
    try {
      // Backend already computes variances per line; we render them as-is.
      const res = await axios.get(
        `${API}/stock/stock-take/submissions?status=pending`
      );
      setPendingSubmissions(res.data || []);
    } catch (err) {
      console.error("Failed to load pending stock takes:", err);
      // Non-admins hit 403 here; only report for real admins.
      if (isAdmin) {
        toast.error(err.response?.data?.detail || "Failed to load pending reviews");
      }
    } finally {
      setPendingLoading(false);
    }
  }, [isAdmin]);

  const loadStockTakeData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/stock/stock-take`);
      setStockData(response.data);
      
      if (response.data.products?.length > 0) {
        const firstCategory = response.data.products[0].category;
        setExpandedCategories({ [firstCategory]: true });
      }
    } catch (error) {
      console.error("Failed to load stock take data:", error);
      toast.error("Failed to load stock data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStockTakeData();
    loadPendingSubmissions();
  }, [loadStockTakeData, loadPendingSubmissions]);

  const handleApproveSubmission = async (submissionId) => {
    setReviewingId(submissionId);
    try {
      const res = await axios.post(
        `${API}/stock/stock-take/review/${submissionId}?action=approve`
      );
      const applied = res.data?.adjustments_applied ?? 0;
      toast.success(`Approved — ${applied} adjustment(s) applied`);
      setExpandedReviewId(null);
      await loadPendingSubmissions();
      // A stock take approval mutates remaining_quantity on units, so
      // the counting UI's baseline needs re-fetching.
      await loadStockTakeData();
    } catch (err) {
      console.error("Approve failed:", err);
      toast.error(err.response?.data?.detail || "Failed to approve");
    } finally {
      setReviewingId(null);
    }
  };

  const handleRejectSubmission = async () => {
    if (!rejectDialog) return;
    const submissionId = rejectDialog.submission_id;
    setReviewingId(submissionId);
    try {
      const params = new URLSearchParams({ action: "reject" });
      if (rejectNotes.trim()) params.append("review_notes", rejectNotes.trim());
      await axios.post(
        `${API}/stock/stock-take/review/${submissionId}?${params.toString()}`
      );
      toast.success("Submission rejected");
      setRejectDialog(null);
      setRejectNotes("");
      setExpandedReviewId(null);
      await loadPendingSubmissions();
    } catch (err) {
      console.error("Reject failed:", err);
      toast.error(err.response?.data?.detail || "Failed to reject");
    } finally {
      setReviewingId(null);
    }
  };

  const formatWhen = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-IE", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleProductCount = (productId, field, value) => {
    if (value === "" || value === null) {
      setProductCounts(prev => {
        const newCounts = { ...prev };
        if (newCounts[productId]) {
          delete newCounts[productId][field];
          if (Object.keys(newCounts[productId]).length === 0) {
            delete newCounts[productId];
          }
        }
        return newCounts;
      });
    } else {
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 0) {
        setProductCounts(prev => ({
          ...prev,
          [productId]: {
            ...(prev[productId] || {}),
            [field]: numValue
          }
        }));
      }
    }
  };

  const getProductCount = (productId, field) => {
    const counts = productCounts[productId];
    if (counts && counts[field] !== undefined) {
      return counts[field];
    }
    return "";
  };

  const hasProductCount = (productId, field) => {
    const counts = productCounts[productId];
    return counts && counts[field] !== undefined;
  };

  const needsFullPartial = (category) => FULL_PARTIAL_CATEGORIES.includes(category);

  const getCountedItemsCount = () => {
    let count = 0;
    Object.values(productCounts).forEach(counts => {
      count += Object.keys(counts).length;
    });
    return count;
  };

  const getTotalItemsCount = () => {
    if (!stockData?.products) return 0;
    let count = 0;
    stockData.products.forEach(product => {
      if (needsFullPartial(product.category)) {
        count += 2; // full + partial
      } else {
        count += 1; // just count
      }
    });
    return count;
  };

  const handleSubmit = async () => {
    const countedItems = [];
    
    stockData.products.forEach(product => {
      const counts = productCounts[product.product_id];
      if (!counts) return;
      
      if (needsFullPartial(product.category)) {
        // Full/partial counting
        if (counts.full !== undefined) {
          countedItems.push({
            unit_id: `full_${product.product_id}`,
            product_id: product.product_id,
            counted_quantity: counts.full,
            count_type: "full"
          });
        }
        if (counts.partial !== undefined) {
          countedItems.push({
            unit_id: `partial_${product.product_id}`,
            product_id: product.product_id,
            counted_quantity: counts.partial,
            count_type: "partial"
          });
        }
      } else {
        // Simple count
        if (counts.count !== undefined) {
          countedItems.push({
            unit_id: `count_${product.product_id}`,
            product_id: product.product_id,
            counted_quantity: counts.count,
            count_type: "total"
          });
        }
      }
    });

    if (countedItems.length === 0) {
      toast.error("Please count at least one item");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/stock/stock-take/submit`, {
        counts: countedItems,
        notes: notes || "Stock take submission"
      });
      
      toast.success(`Stock take submitted! ${response.data.variances_found} variance(s) found. Pending admin review.`);
      
      setProductCounts({});
      setNotes("");
      loadStockTakeData();
    } catch (error) {
      console.error("Failed to submit stock take:", error);
      toast.error(error.response?.data?.detail || "Failed to submit stock take");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setProductCounts({});
    setNotes("");
    toast.info("Counts cleared");
  };

  const productsByCategory = {};
  if (stockData?.products) {
    stockData.products.forEach(product => {
      const cat = product.category;
      if (!productsByCategory[cat]) {
        productsByCategory[cat] = [];
      }
      productsByCategory[cat].push(product);
    });
  }

  const categories = Object.keys(productsByCategory);
  const countedCount = getCountedItemsCount();
  const totalCount = getTotalItemsCount();

  if (isLoading) {
    return (
      <Card className="bg-saturn-slate border-slate-800">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-saturn-orange" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pending Reviews (admin-only). Only rendered when there is something
          to review — an empty state on a screen that's already busy with
          the counting UI is just clutter. Positioned above the counting
          card so an outstanding submission can never be forgotten. */}
      {isAdmin && pendingSubmissions.length > 0 && (
        <Card className="bg-saturn-slate border-amber-500/40" data-testid="stock-take-pending-reviews">
          <CardHeader className="border-b border-slate-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-white flex items-center gap-2 min-w-0">
                <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="truncate">Pending Reviews</span>
                <span className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-xs px-2 py-0.5 rounded-full">
                  {pendingSubmissions.length}
                </span>
              </CardTitle>
              {pendingLoading && (
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {pendingSubmissions.map((sub) => {
              const isExpanded = expandedReviewId === sub.submission_id;
              const isReviewing = reviewingId === sub.submission_id;
              const variances = sub.line_variances || [];
              // Backend returns line_variances[]; some legacy submissions
              // may not have it computed — guard for that.
              const variancesWithDelta = variances.filter(
                (v) => Math.abs(v.variance ?? 0) > 0.001
              );
              return (
                <div
                  key={sub.submission_id}
                  className="bg-slate-800/40 rounded-lg border border-slate-700 overflow-hidden"
                  data-testid={`pending-submission-${sub.submission_id}`}
                >
                  <div className="p-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-400">
                          {sub.submission_id?.slice(-10)}
                        </span>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatWhen(sub.submitted_at)}
                        </span>
                        {variancesWithDelta.length > 0 ? (
                          <span className="text-xs px-2 py-0.5 rounded border bg-amber-500/20 text-amber-300 border-amber-500/40 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {variancesWithDelta.length} variance{variancesWithDelta.length === 1 ? "" : "s"}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded border bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                            No variances
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-300 mt-1">
                        By <span className="text-white">{sub.submitted_by_name || sub.submitted_by_email || "unknown"}</span>
                        {sub.notes && (
                          <span className="text-slate-500"> · {sub.notes}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedReviewId(isExpanded ? null : sub.submission_id)}
                        className="text-slate-300 hover:bg-slate-700"
                        data-testid={`toggle-review-${sub.submission_id}`}
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        {isExpanded ? "Hide" : "View"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApproveSubmission(sub.submission_id)}
                        disabled={isReviewing}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`approve-submission-${sub.submission_id}`}
                      >
                        {isReviewing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setRejectDialog(sub); setRejectNotes(""); }}
                        disabled={isReviewing}
                        className="border-red-500/50 text-red-300 hover:bg-red-500/20"
                        data-testid={`reject-submission-${sub.submission_id}`}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-slate-700 bg-slate-900/50 p-3">
                      <table className="w-full text-sm">
                        <thead className="text-xs text-slate-500 uppercase">
                          <tr>
                            <th className="text-left py-1">Product</th>
                            <th className="text-right py-1">System</th>
                            <th className="text-right py-1">Counted</th>
                            <th className="text-right py-1">Δ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {variances.length === 0 && (
                            <tr>
                              <td colSpan={4} className="text-slate-500 py-3 text-center italic">
                                No line-level variances recorded on this submission.
                              </td>
                            </tr>
                          )}
                          {variances.map((v, idx) => {
                            const delta = v.variance ?? 0;
                            const dColor =
                              Math.abs(delta) < 0.001 ? "text-slate-500"
                              : delta > 0 ? "text-emerald-400"
                              : "text-red-400";
                            return (
                              <tr key={idx} className="border-t border-slate-800">
                                <td className="py-1.5 text-slate-300">
                                  {v.product_name}
                                  {v.count_type && v.count_type !== "total" && (
                                    <span className="text-xs text-slate-500 ml-1">({v.count_type})</span>
                                  )}
                                </td>
                                <td className="py-1.5 text-right text-slate-400 font-mono">
                                  {v.system_display ?? v.system_quantity ?? "—"}
                                </td>
                                <td className="py-1.5 text-right text-white font-mono">
                                  {v.counted_display ?? v.counted_quantity ?? "—"}
                                </td>
                                <td className={`py-1.5 text-right font-mono font-semibold ${dColor}`}>
                                  {Math.abs(delta) < 0.001 ? "—"
                                    : (delta > 0 ? "+" : "") + (v.variance_display ?? delta)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <p className="text-xs text-slate-500 mt-2">
                        Approving applies every counted value as the new
                        <code className="mx-1 text-slate-400">remaining_quantity</code>
                        on each unit. Rejecting keeps stock levels untouched.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

    <Card className="bg-saturn-slate border-slate-800">
      <CardHeader className="border-b border-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-white flex items-center gap-2 min-w-0">
            <ClipboardCheck className="w-5 h-5 text-saturn-orange flex-shrink-0" />
            <span className="truncate">Stock Take</span>
          </CardTitle>
          <div className="flex items-center flex-wrap gap-2 sm:gap-3">
            <span className="text-sm text-slate-400 whitespace-nowrap">
              {countedCount} / {totalCount} counted
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleReset}
              disabled={countedCount === 0}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="stock-take-reset-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={countedCount === 0 || isSubmitting}
              className="bg-saturn-orange hover:bg-orange-600"
              data-testid="stock-take-submit-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-1" />
              )}
              Submit for Review
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm text-blue-300">
          <strong>Instructions:</strong><br/>
          • <strong>Films & Chemicals:</strong> Count full units and partial units separately<br/>
          • <strong>Blades, Tools, Consumables:</strong> Count total packs/pieces<br/>
          • Enter 0 if none found. Submissions reviewed by admin.
        </div>

        <div className="space-y-2">
          {categories.map(category => {
            const CategoryIcon = CATEGORY_ICONS[category] || Package;
            const products = productsByCategory[category];
            const isExpanded = expandedCategories[category];
            const isFullPartial = needsFullPartial(category);
            
            let categoryCounted = 0;
            let categoryTotal = 0;
            products.forEach(p => {
              if (isFullPartial) {
                categoryTotal += 2;
                if (hasProductCount(p.product_id, "full")) categoryCounted++;
                if (hasProductCount(p.product_id, "partial")) categoryCounted++;
              } else {
                categoryTotal += 1;
                if (hasProductCount(p.product_id, "count")) categoryCounted++;
              }
            });

            return (
              <Collapsible
                key={category}
                open={isExpanded}
                onOpenChange={() => toggleCategory(category)}
              >
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                      <CategoryIcon className="w-5 h-5 text-saturn-orange" />
                      <span className="font-medium text-white">{category}</span>
                      <span className="text-sm text-slate-400">
                        ({products.length} products)
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      categoryCounted === categoryTotal && categoryTotal > 0
                        ? "bg-green-500/20 text-green-400"
                        : categoryCounted > 0
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-slate-700 text-slate-400"
                    }`}>
                      {categoryCounted}/{categoryTotal}
                    </span>
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="mt-2 space-y-2 pl-4">
                    {products.map(product => {
                      const hasFullCount = hasProductCount(product.product_id, "full");
                      const hasPartialCount = hasProductCount(product.product_id, "partial");
                      const hasTotalCount = hasProductCount(product.product_id, "count");
                      
                      // Determine unit label based on category
                      let unitLabel = "packs";
                      if (category === "Chemicals/Liquids") unitLabel = "bottles";
                      else if (category === "Films/Wraps") unitLabel = "rolls";
                      else if (category === "Consumables") unitLabel = "packs";
                      else if (category === "Blades/Cutting tools") unitLabel = "packs";
                      else if (category === "Tools/Equipment") unitLabel = "items";
                      
                      return (
                        <div key={product.product_id} className="bg-slate-800/30 rounded-lg p-3">
                          <h4 className="font-medium text-white mb-2">{product.product_name}</h4>
                          
                          {isFullPartial ? (
                            <div className="space-y-2">
                              {/* Full count */}
                              <div className={`flex items-center justify-between p-2 rounded ${
                                hasFullCount ? "bg-green-500/10 border border-green-500/30" : "bg-slate-700/30"
                              }`}>
                                <div className="flex items-center gap-2">
                                  <span className="px-1.5 py-0.5 rounded text-xs bg-green-500/20 text-green-400">
                                    Full
                                  </span>
                                  <span className="text-sm text-slate-300">
                                    How many full {unitLabel}?
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    placeholder="—"
                                    value={getProductCount(product.product_id, "full")}
                                    onChange={(e) => handleProductCount(product.product_id, "full", e.target.value)}
                                    className={`w-16 h-8 text-center bg-slate-700 border-slate-600 text-white ${
                                      hasFullCount ? "border-green-500" : ""
                                    }`}
                                    data-testid={`stock-take-full-${product.product_id}`}
                                  />
                                  {hasFullCount && <Check className="w-4 h-4 text-green-500" />}
                                </div>
                              </div>
                              
                              {/* Partial count */}
                              <div className={`flex items-center justify-between p-2 rounded ${
                                hasPartialCount ? "bg-green-500/10 border border-green-500/30" : "bg-slate-700/30"
                              }`}>
                                <div className="flex items-center gap-2">
                                  <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400">
                                    Partial
                                  </span>
                                  <span className="text-sm text-slate-300">
                                    How many partial {unitLabel}?
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    placeholder="—"
                                    value={getProductCount(product.product_id, "partial")}
                                    onChange={(e) => handleProductCount(product.product_id, "partial", e.target.value)}
                                    className={`w-16 h-8 text-center bg-slate-700 border-slate-600 text-white ${
                                      hasPartialCount ? "border-green-500" : ""
                                    }`}
                                    data-testid={`stock-take-partial-${product.product_id}`}
                                  />
                                  {hasPartialCount && <Check className="w-4 h-4 text-green-500" />}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Simple total count for blades, tools, consumables
                            <div className={`flex items-center justify-between p-2 rounded ${
                              hasTotalCount ? "bg-green-500/10 border border-green-500/30" : "bg-slate-700/30"
                            }`}>
                              <span className="text-sm text-slate-300">
                                How many {unitLabel}?
                              </span>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min="0"
                                  placeholder="—"
                                  value={getProductCount(product.product_id, "count")}
                                  onChange={(e) => handleProductCount(product.product_id, "count", e.target.value)}
                                  className={`w-16 h-8 text-center bg-slate-700 border-slate-600 text-white ${
                                    hasTotalCount ? "border-green-500" : ""
                                  }`}
                                  data-testid={`stock-take-count-${product.product_id}`}
                                />
                                {hasTotalCount && <Check className="w-4 h-4 text-green-500" />}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>

        <div className="pt-4 border-t border-slate-700">
          <label className="text-sm text-slate-400 block mb-2">
            Notes (optional)
          </label>
          <Input
            placeholder="Any notes about this stock take..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white"
            data-testid="stock-take-notes"
          />
        </div>
      </CardContent>
    </Card>

    {/* Reject-with-notes dialog. Separate from approve because a reject
        is a deliberate destructive action — we ask for a reason so the
        audit trail explains why the counted values weren't applied. */}
    <Dialog open={!!rejectDialog} onOpenChange={(o) => { if (!o) { setRejectDialog(null); setRejectNotes(""); } }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <X className="w-5 h-5" />
            Reject stock take submission?
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Stock levels will stay unchanged. This is permanent — the submission
            will move to <span className="text-white font-medium">rejected</span> status.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-2">
          <label className="text-sm text-slate-400 block">
            Reason (optional but recommended)
          </label>
          <Input
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            placeholder="e.g. counts obviously wrong, will recount"
            className="bg-slate-800 border-slate-700 text-white"
            data-testid="reject-notes-input"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => { setRejectDialog(null); setRejectNotes(""); }}
            className="border-slate-600 text-slate-300"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectSubmission}
            disabled={reviewingId === rejectDialog?.submission_id}
            className="bg-red-600 hover:bg-red-700"
            data-testid="confirm-reject-submission"
          >
            {reviewingId === rejectDialog?.submission_id
              ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
              : <X className="w-4 h-4 mr-1" />}
            Reject submission
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
  );
};

export default StockTakeSection;
